import { Outlet, createRootRoute, useLocation, useMatches } from '@tanstack/react-router'
import { lazy, Suspense, useEffect } from 'react'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import Nav from '../components/Nav'
import MobileTabBar from '@/components/MobileTabBar'
// Lazy: NewEventToasts is the only consumer of lottie-web (~320 kB); loading
// it on demand keeps the animation library out of the entry bundle.
const NewEventToasts = lazy(() => import('@/components/NewEventToasts'))
import GuidedWalkthrough from '@/components/GuidedWalkthrough'
import { MarketingFooter } from '@/components/MarketingFooter'
import PageTransition from '@/components/PageTransition';
import { useUser as useClerkUser } from '@clerk/clerk-react'
import { getUserFromClerkId } from '@/hooks/useUser'
import SubscriptionModal from '@/components/SubscriptionModal'
import PendingInviteRedirect from '@/components/PendingInviteRedirect'
// import PendingWelcomeKitsBanner from '@/components/PendingWelcomeKitsBanner';
// import { getPendingWelcomeKits } from '@/hooks/useWelcomeKit';
// import { isPendingWelcomeKit } from '@/lib/welcomeKit';
import { useGeolocation } from '@/hooks/useGeolocation';
import { getCookie, setCookie } from '@/lib/cookies';
import { userHasPaidAccess } from '@/lib/userSubscriptionHelpers';
import { AppTourProvider } from '@/contexts/AppTourContext';
import { identifyUser, resetPostHog, setUserProperties, groupUser } from '@/lib/posthog';
import RouteErrorScreen from '@/components/RouteErrorScreen';

export const Route = createRootRoute({
  errorComponent: RouteErrorScreen,
  component: () => {
    const location = useLocation()
    const matches = useMatches()

    // Full-screen immersive routes: no nav bar, no footer.
    // Only the slide player routes, not the course overview page.
    const fullScreenPages = [
      "/lessons",
      "/learning-journeys/new-age-of-scams",
      "/learning-journeys/emotional-hijack",
      "/learning-journeys/pause-and-verify",
      "/learning-journeys/warning-signs",
      "/learning-journeys/digital-self-defence",
      "/learning-journeys/what-to-do",
      "/learning-journeys/what-to-do-after-a-scam",
      "/toolkit",
      "/onboarding",
    ]

    function normalizePath(path: string) {
      return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
    }

    const matchesAny = (pages: string[]) =>
      pages.some(base => {
        const normalizedBase = normalizePath(base);
        const normalizedPath = normalizePath(location.pathname);
        return (
          normalizedPath === normalizedBase ||
          normalizedPath.startsWith(normalizedBase + "/")
        );
      })

    const isFullScreen = matchesAny(fullScreenPages)
    // Nav shows on every signed-in (_auth) page except full-screen flows.
    // Deny-listing full-screen routes (instead of the old allow-list of nav
    // pages) means new routes get the nav by default and pages like /search,
    // /saved, and /missions stop rendering chrome-less.
    const isAuthArea = matches.some((m) => m.routeId.startsWith('/_auth'))
    const showNav = isAuthArea && !isFullScreen

    const { user: clerkUser, isLoaded: clerkLoaded } = useClerkUser();
    const clerkId = clerkUser?.id ?? '';
    const { data: user, isLoading: userLoading } = getUserFromClerkId(clerkId, { enabled: !!clerkId });

    // const { data: pendingKitsData } = getPendingWelcomeKits();
    // const isPendingKit = isPendingWelcomeKit(pendingKitsData);

    const hasPaidAccess = userHasPaidAccess(user);

    const allowedPages = ['/account', '/invite', '/checkout-success', '/checkout-cancel', '/subscribe', '/signing-in', '/register', '/signin', '/signup'];
    const isAllowedPage = allowedPages.includes(location.pathname);


    // Gate: show subscription modal for any signed-in user who doesn't have
    // confirmed paid access. This covers both the normal case (user data loaded,
    // no active subscription) and the error case (user query failed, so we can't
    // confirm access — safer to gate than to give free access).
    const showSubscriptionModal =
      clerkLoaded && !!clerkUser && !userLoading &&
      !hasPaidAccess &&
      !isAllowedPage;

    const CURRENCY_COOKIE = 'gg_currency';

    const hasCurrencyCookie = !!getCookie(CURRENCY_COOKIE);
    const { data: geo } = useGeolocation({ enabled: !hasCurrencyCookie, retry: false });

    useEffect(() => {
      if (!hasCurrencyCookie && geo?.currency) {
        setCookie(CURRENCY_COOKIE, geo.currency, { maxAgeSeconds: 60 * 60 * 24 * 30, sameSite: 'Lax' });
      }
    }, [hasCurrencyCookie, geo?.currency]);

    // PostHog: identify user when signed in, reset on sign out
    useEffect(() => {
      if (clerkLoaded && clerkUser) {
        identifyUser(clerkUser.id, {
          email: clerkUser.primaryEmailAddress?.emailAddress,
          name: user?.name || [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(' ') || undefined,
          market: (user as any)?.market,
          role: (user as any)?.role,
          license: (user as any)?.license,
          onboarding_step: (user as any)?.onboardingStep,
          has_parent: !!(user as any)?.parentUserId,
          is_test_account: !!(user as any)?.isTestAccount,
        });
        // Group by household (the sponsoring parent account, or self if they
        // are the account head) so engagement can be measured per family.
        const householdId = (user as any)?.parentUserId || (user as any)?.id;
        if (householdId) {
          groupUser('household', householdId, {
            is_household_head: !(user as any)?.parentUserId,
            market: (user as any)?.market,
          });
        }
      } else if (clerkLoaded && !clerkUser) {
        resetPostHog();
      }
    }, [clerkLoaded, clerkUser?.id, user?.name, (user as any)?.license, (user as any)?.onboardingStep]);

    // Keep PostHog properties in sync when user data changes
    useEffect(() => {
      if (user && clerkUser) {
        setUserProperties({
          license: (user as any)?.license,
          onboarding_step: (user as any)?.onboardingStep,
          has_paid_access: hasPaidAccess,
        });
      }
    }, [(user as any)?.license, (user as any)?.onboardingStep, hasPaidAccess]);

    // const normalizedPath = normalizePath(location.pathname);
    // const isOnboardingPage = normalizedPath === '/onboarding' || normalizedPath.startsWith('/onboarding/');

    return (
      <AppTourProvider>
        {/* <BigCursor color="#7C3AED" size={20} showRipple={true} /> */}
        {showNav && <Nav />}
        {showNav && <MobileTabBar />}

        {/* {isPendingKit && !isOnboardingPage && <PendingWelcomeKitsBanner />} */}
        <SubscriptionModal showModal={showSubscriptionModal} userId={user?.id} />
        <PendingInviteRedirect />
        {/* pb-16 on phones keeps the fixed bottom tab bar from covering
            page content; sm:pb-0 because the bar is hidden from sm: up. */}
        <div className={`w-full mx-auto ${showNav ? 'pb-16 sm:pb-0' : ''}`}>
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
        {showNav && clerkLoaded && !!clerkUser && hasPaidAccess && <GuidedWalkthrough />}
        {/* Mounted globally so chapter-complete and other point-reward toasts
            appear on full-screen routes (slide wizards) where Nav is hidden. */}
        {clerkLoaded && !!clerkUser && (
          <Suspense fallback={null}>
            <NewEventToasts />
          </Suspense>
        )}
        {!isAllowedPage && !isFullScreen && <MarketingFooter />}
        <TanStackRouterDevtools />
      </AppTourProvider>
    )
  },
})
